import React from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { Box, Flex } from 'rebass';
import { useStore } from 'effector-react';
import dayjs from 'dayjs';

import { Trans, t } from '@lingui/macro';
import { Button, Typography, Dialog } from '@oca/ui';
import { TextFieldWrapper } from '@lib/final-form-oca-ui';
import { i18n } from '@lib/i18n';
import {
  $decisionTarget,
  closeDecisionModal,
  decidePayrollEffect,
  toSubmitErrors,
} from '../model/payroll';

const REASON_MAX = 1000;
const NOTES_MAX = 4000;

export const DecisionModal = ({ getEmployeeName, formatPeriod }) => {
  const target = useStore($decisionTarget);

  if (!target) return null;

  const handleSubmit = async ({ decision, rejectionReason, notes }) => {
    const trimmedNotes = (notes || '').trim();
    try {
      await decidePayrollEffect({
        id: target.id,
        status: decision,
        // Must be omitted for approvals, required for rejections.
        ...(decision === 'rejected'
          ? { rejectionReason: rejectionReason.trim() }
          : {}),
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      });
      return null;
    } catch (error) {
      return toSubmitErrors(error, ['status', 'rejectionReason', 'notes']);
    }
  };

  return (
    <Dialog
      title={i18n._(t`Review Payroll Slip Request`)}
      onClose={closeDecisionModal}
    >
      <Box style={{ minWidth: 400, maxWidth: 460 }}>
        <Box mb={3} p={3} bg="#f9f9fb" style={{ borderRadius: 8 }}>
          <Typography fontSize={14} fontWeight={600} mb={1}>
            {getEmployeeName(target)}
          </Typography>
          <Detail label={<Trans>Period</Trans>}>{formatPeriod(target)}</Detail>
          <Detail label={<Trans>Reason</Trans>}>{target.reason}</Detail>
          {target.notes && (
            <Detail label={<Trans>Employee notes</Trans>}>
              {target.notes}
            </Detail>
          )}
          {target.createdAt && (
            <Detail label={<Trans>Submitted</Trans>}>
              {dayjs(target.createdAt).format('YYYY-MM-DD HH:mm')}
            </Detail>
          )}
        </Box>

        <Form
          initialValues={{ decision: null }}
          validate={validate}
          onSubmit={handleSubmit}
          render={({ handleSubmit: submit, form, submitting, submitError }) => {
            const decide = decision => {
              form.change('decision', decision);
              submit();
            };

            return (
              <form onSubmit={submit}>
                <Box mb={20}>
                  <Field
                    name="rejectionReason"
                    label={<Trans>Rejection reason (required to reject)</Trans>}
                    placeholder={i18n._(t`e.g. Period not available`)}
                    component={TextFieldWrapper}
                    maxLength={REASON_MAX}
                    spaceAfter
                  />
                </Box>
                <Box mb={20}>
                  <Field
                    name="notes"
                    label={<Trans>Internal note (optional)</Trans>}
                    placeholder={i18n._(t`Visible to reviewers only`)}
                    component={TextFieldWrapper}
                    maxLength={NOTES_MAX}
                    spaceAfter
                  />
                </Box>

                {submitError && (
                  <Box
                    mb={3}
                    p={2}
                    bg="#fff1f0"
                    style={{ border: '1px solid #ffa39e', borderRadius: 6 }}
                  >
                    <Typography color="#cf1322" fontSize={13}>
                      {submitError}
                    </Typography>
                  </Box>
                )}

                <Flex justifyContent="flex-end" style={{ gap: '10px' }}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeDecisionModal}
                    disabled={submitting}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    disabled={submitting}
                    onClick={() => decide('rejected')}
                  >
                    <Trans>Reject</Trans>
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={submitting}
                    onClick={() => decide('approved')}
                  >
                    <Trans>Approve</Trans>
                  </Button>
                </Flex>
              </form>
            );
          }}
        />
      </Box>
    </Dialog>
  );
};

DecisionModal.propTypes = {
  getEmployeeName: PropTypes.func.isRequired,
  formatPeriod: PropTypes.func.isRequired,
};

function Detail({ label, children }) {
  return (
    <Typography fontSize={13} color="textSecondary" mb={1}>
      {label}: {children}
    </Typography>
  );
}

Detail.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

function validate({ decision, rejectionReason, notes }) {
  const errors = {};
  const reason = (rejectionReason || '').trim();

  if (decision === 'rejected' && !reason) {
    errors.rejectionReason = (
      <Trans>A rejection reason is required to reject</Trans>
    );
  }
  if (reason.length > REASON_MAX) {
    errors.rejectionReason = (
      <Trans>Rejection reason must be at most 1000 characters</Trans>
    );
  }
  if (decision === 'approved' && reason) {
    errors.rejectionReason = (
      <Trans>Clear the rejection reason before approving</Trans>
    );
  }
  if (notes && notes.length > NOTES_MAX) {
    errors.notes = <Trans>Notes must be at most 4000 characters</Trans>;
  }
  return errors;
}
